import { Component, DestroyRef, Inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AdminScopes, AdminUser, UserService } from '@app/shared/services/user.service';
import { FileUploadComponent } from '@app/pages/authenticated/components/file-upload/file-upload.component';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LoggerService } from '@app/shared/services/logger.service';
import { BannerNotificationsService } from '@app/shared/services/banner-notifications.service';
import { MTX_DRAWER_DATA, MtxDrawerRef } from '@ng-matero/extensions/drawer';
import { DateFormats, DefaultTimezone, ShippingProviderNames, SupportedReconProvider } from '@app/shared/constants';
import { HelpersService } from '@app/shared/services/helpers.service';
import { DateTime } from 'luxon';
import {
  COURIER_INVOICE_REJECT_LABELS,
  CourierInvoiceUploadRejectCode,
  CourierInvoiceUploadResult,
  DuplicateOfEntry,
  FinanceService,
} from '../../finance.service';

// One rejected Uber file's duplicate warning, ready for the template: the messages to show (one
// per matched row in `duplicateOf`) and whether "Upload anyway" applies. `canUploadAnyway` is true
// only when every matched entry is a fileName-only match — a BillID match is a hard block, and
// mixing the two in one file keeps it blocked (allowNameMatch never overrides a BillID match).
export interface DuplicateWarningView {
  originalFileName: string;
  entries: DuplicateOfEntry[];
  canUploadAnyway: boolean;
}

// Providers whose upload accepts several files in one request. Component-local: mirrors the
// app-services `MULTI_FILE_PROVIDERS` string list (no cross-app import available). Today only
// Uber — every other provider keeps the exactly-one-file rule byte-identical.
export const MULTI_FILE_PROVIDERS: SupportedReconProvider[] = [SupportedReconProvider.UBER];

// Client-side mirror of the app-services aggregate caps (`finance/invoices` interceptor
// options: maxFiles: 10, maxTotalBytes: 40 MiB). Enforced here only for a fast, friendly error —
// the server enforces the real limit regardless.
const MAX_MULTI_FILE_COUNT = 10;
const MAX_MULTI_FILE_TOTAL_BYTES = 40 * 1024 * 1024;

@Component({
  standalone: false,
  selector: 'app-upload-courier-invoice',
  templateUrl: './upload-courier-invoice.component.html',
})
export class UploadCourierInvoiceComponent implements OnInit {
  public adminUser: AdminUser;
  public form: FormGroup;
  public loading: boolean = false;
  public courierInvoiceFile: any;
  public courierInvoiceFiles: File[] = [];
  // Driven by the provider control's value — true only for MULTI_FILE_PROVIDERS (Uber today).
  public isMultiFileMode: boolean = false;
  // One entry per still-rejected 'duplicate-invoice' file, rebuilt on every response (initial
  // submit or an "Upload anyway" resubmit) — see `updateDuplicateWarnings`. Rendered in the
  // drawer body rather than the banner so the fileName-only case can carry its own button.
  public duplicateWarnings: DuplicateWarningView[] = [];

  public readonly issuerOptions = [];

  // The child's displayed chip list must be pruned alongside `courierInvoiceFiles` after a partial
  // rejection; it is undefined only in specs that override the template away.
  @ViewChild(FileUploadComponent) private fileUpload?: FileUploadComponent;

  constructor(
    private userService: UserService,
    private formBuilder: FormBuilder,
    private logger: LoggerService,
    private bannerNotificationsService: BannerNotificationsService,
    private helpersService: HelpersService,
    private destroyRef: DestroyRef,
    public drawerRef: MtxDrawerRef<UploadCourierInvoiceComponent>,
    private financeService: FinanceService,
    @Inject(MTX_DRAWER_DATA)
    public data: {
      adminUser: AdminUser;
    }
  ) {}

  ngOnInit() {
    if (
      this.data &&
      this.data.adminUser &&
      this.userService.hasAuthScope(this.data.adminUser, [AdminScopes.MODIFY_FINANCE])
    ) {
      this.adminUser = this.data.adminUser;
      this.createForm();
      this.form
        .get('provider')!
        .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((provider: SupportedReconProvider) => this.onProviderChange(provider));
    } else {
      this.dismissDrawer();
    }
  }

  public submitForm() {
    this.loading = true;
    const formValues = this.form.value;
    const params = {
      courier: formValues.provider,
      invoiceNumber: formValues.invoiceNumber,
    };
    const formData = new FormData();
    formData.append('dto', JSON.stringify(params));

    if (this.isMultiFileMode) {
      this.courierInvoiceFiles.forEach((file) => formData.append('courierInvoiceFile', file, file.name));
    } else if (this.courierInvoiceFile) {
      formData.append('courierInvoiceFile', this.courierInvoiceFile, this.courierInvoiceFile.name);
    }

    this.financeService.uploadCourierInvoice(formData).subscribe({
      next: (res) => this.handleUploadSuccess(res),
      error: (err) => this.handleUploadError(err),
    });
  }

  // Bound to `(uploadedFileEvent)`. `file-upload` emits a single `File` when `multiple` is
  // false and the full `File[]` when `multiple` is true — this handler branches on
  // `isMultiFileMode`, which always matches the `[multiple]` binding in the template.
  public updateUploadedFiles(files: File | File[]): void {
    if (this.isMultiFileMode) {
      const fileArray = Array.isArray(files) ? files : files ? [files] : [];
      const validationError = this.validateMultiFileSelection(fileArray);
      if (validationError) {
        this.bannerNotificationsService.error(validationError);
        this.courierInvoiceFiles = [];
        this.form.get('file')!.patchValue('');
        this.pruneStaleDuplicateWarnings();
        return;
      }
      this.courierInvoiceFiles = fileArray;
      this.pruneStaleDuplicateWarnings();
    } else {
      this.courierInvoiceFile = Array.isArray(files) ? files[0] : files;
    }
  }

  public onValidationError(error: any): void {
    this.bannerNotificationsService.error(error);
  }

  public dismissDrawer(successful: boolean = false): void {
    this.drawerRef.dismiss(successful);
  }

  private onProviderChange(provider: SupportedReconProvider): void {
    this.isMultiFileMode = MULTI_FILE_PROVIDERS.includes(provider);
    // Reset both the file selection and the invoice number on every provider switch — a file (or
    // invoice number) picked for the previous provider must never be silently carried over.
    this.courierInvoiceFile = undefined;
    this.courierInvoiceFiles = [];
    // A duplicate warning names a specific file from the OLD selection — carrying it across a
    // provider switch leaves a stale warning on screen (its "Upload anyway" would silently do
    // nothing, since `uploadAnyway` can no longer find the file it names in the new selection).
    this.duplicateWarnings = [];
    this.form.get('file')!.patchValue('');
    this.form.get('invoiceNumber')!.patchValue('');
  }

  // Drops any duplicate warning for a file no longer in the current selection. Called after
  // every selection change (`updateUploadedFiles`) — a file removed via the file-upload chip list,
  // or replaced by a fresh selection, otherwise leaves its warning on screen indefinitely: it
  // blocks the "nothing left outstanding" auto-close check forever, and its "Upload anyway" button
  // silently does nothing once `uploadAnyway` can no longer find the file it names.
  private pruneStaleDuplicateWarnings(): void {
    const selectedNames = new Set(this.courierInvoiceFiles.map((file) => file.name));
    this.duplicateWarnings = this.duplicateWarnings.filter((warning) => selectedNames.has(warning.originalFileName));
  }

  private validateMultiFileSelection(files: File[]): string | null {
    if (!files.length) {
      return null;
    }
    if (files.length > MAX_MULTI_FILE_COUNT) {
      return `Select at most ${MAX_MULTI_FILE_COUNT} CSV files (up to 40 MiB total).`;
    }
    const nonCsv = files.find((file) => !file.name.toLowerCase().endsWith('.csv'));
    if (nonCsv) {
      return `${nonCsv.name} is not a .csv file — only CSV files are supported.`;
    }
    const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
    if (totalBytes > MAX_MULTI_FILE_TOTAL_BYTES) {
      return 'Selected files total more than 40 MiB — split the upload into smaller batches.';
    }
    return null;
  }

  private handleUploadSuccess(res: boolean | CourierInvoiceUploadResult): void {
    this.loading = false;
    if (this.isMultiFileMode && this.isUploadResult(res)) {
      this.handleMultiFileUploadSuccess(res);
      return;
    }
    // Sets the correct expectation: the HTTP response only means the file was accepted and
    // enqueued, not that reconciliation has finished. Large invoices can take minutes to
    // fully process — see the Courier Invoices list's row-count status for progress.
    this.bannerNotificationsService.success(
      'Invoice accepted. Reconciliation is running in the background — large invoices can take ' +
        'several minutes, and adjustments will appear on the Reconciliation page as they are processed.'
    );
    this.logger.log('Add Courier Invoice success', res);
    this.dismissDrawer(true);
  }

  private handleMultiFileUploadSuccess(result: CourierInvoiceUploadResult): void {
    if (result.accepted.length > 0) {
      const totalBills = result.accepted.reduce((sum, file) => sum + file.bills.length, 0);
      const acceptedLines = result.accepted.map((file) => {
        const billIds = file.bills.map((bill) => bill.billId).join(', ');
        const awaitingConfirmation = file.code === 'enqueue-unconfirmed' ? ' — awaiting confirmation' : '';
        return `${this.displayFileName(file)}: ${billIds}${awaitingConfirmation}`;
      });
      const summary = `Accepted ${result.accepted.length} file(s) (${totalBills} bill(s)); reconciliation is running in the background`;
      this.bannerNotificationsService.success([summary, ...acceptedLines].join('\n'));

      // Prune every accepted file from the selection whenever ANY file was accepted, not only
      // when this response also carries a rejection. An "Upload anyway" resubmit only ever sends
      // one file — without this, a resolved duplicate stayed selected, and pressing Save again
      // re-sent it: money-safe (Redis dedupe) but a fresh GCS object and a `recon_audits` row
      // stuck in `processing` for that BillID.
      this.pruneAcceptedFiles(result);
    }

    this.updateDuplicateWarnings(result);

    // 'duplicate-invoice' rejections are rendered as dedicated warnings in the drawer body (see
    // `duplicateWarnings` above), not in this generic banner list.
    const nonDuplicateRejected = this.nonDuplicateRejected(result.rejected);
    if (nonDuplicateRejected.length > 0) {
      this.bannerNotificationsService.error(
        [
          ...this.formatRejectedLines(nonDuplicateRejected),
          'The accepted file(s) were removed from the selection — fix or remove the rejected file(s) and save again.',
        ].join('\n')
      );
    }

    this.logger.log('Add Courier Invoice success (Uber multi-file)', result);
    // Close the drawer once nothing is left needing attention: no file still selected (every
    // accepted file was just pruned above; a rejected or untouched file stays selected) and no
    // duplicate warning outstanding from an earlier response. This applies the same way whether
    // this came from the main Save or from an "Upload anyway" resubmit — a resubmit that
    // clears the last outstanding file or warning now closes the drawer too, instead of leaving it
    // open with nothing left to do.
    if (this.courierInvoiceFiles.length === 0 && this.duplicateWarnings.length === 0) {
      this.dismissDrawer(true);
    }
  }

  private handleUploadError(err: any, dismissOnFail: boolean = true): void {
    this.loading = false;
    // Uber multi-file path: when EVERY file is rejected app-services throws 400 with
    // `{ message: 'All uploaded files were rejected', rejected: [{ fileName, code }] }` — no
    // top-level `code`. Render the same per-file lines as a partial rejection and keep the drawer
    // (and the whole selection) open so finance can fix or remove the bad file(s) and resubmit.
    const rejected = this.rejectedFilesFrom(err);
    if (rejected) {
      this.updateDuplicateWarnings({ accepted: [], rejected });
      const nonDuplicateRejected = this.nonDuplicateRejected(rejected);
      if (nonDuplicateRejected.length > 0) {
        this.bannerNotificationsService.error(
          ['No files were accepted:', ...this.formatRejectedLines(nonDuplicateRejected)].join('\n')
        );
      } else {
        // Every rejected file is 'duplicate-invoice' -- the per-file "Already uploaded on..." text
        // is already rendered in the drawer body via updateDuplicateWarnings above, so this banner
        // only needs to point finance at it instead of repeating the detail.
        this.bannerNotificationsService.error(
          rejected.length > 1
            ? 'These invoices were already uploaded — see the details in the upload panel.'
            : 'This invoice was already uploaded — see the details in the upload panel.'
        );
      }
      this.logger.error('Add Courier Invoice error (all files rejected)', err);
      return;
    }
    // The backend returns the rejection body as the HTTP error (finance.service unwraps
    // `error.error`), so `err` here is normally the `{ message, code }` object directly; `err.error`
    // is checked too in case an error reaches this handler still wrapped. A recognized code gets
    // its human label; anything else (network error, ErrorMessages.UNEXPECTED string, unknown code)
    // falls back to the generic message.
    const code: CourierInvoiceUploadRejectCode | undefined = err?.code ?? err?.error?.code;
    const message = code && COURIER_INVOICE_REJECT_LABELS[code]
      ? COURIER_INVOICE_REJECT_LABELS[code]
      : 'Saving Courier Invoice failed';
    this.bannerNotificationsService.error(message);
    this.logger.error('Add Courier Invoice error', err);
    if (dismissOnFail) {
      this.dismissDrawer(false);
    }
  }

  private isUploadResult(res: any): res is CourierInvoiceUploadResult {
    return !!res && typeof res === 'object' && Array.isArray(res.accepted) && Array.isArray(res.rejected);
  }

  // One `<file name>: <human label>` line per rejected file — shared by the partial-rejection
  // success path and the all-rejected 400 error path so both read identically.
  private formatRejectedLines(rejected: CourierInvoiceUploadResult['rejected']): string[] {
    return rejected.map((file) => `${this.displayFileName(file)}: ${COURIER_INVOICE_REJECT_LABELS[file.code]}`);
  }

  // Banners name the file as finance uploaded it. `fileName` is the server's stored (suffixed)
  // name — only shown when a response predates `originalFileName`.
  private displayFileName(file: { fileName: string; originalFileName?: string }): string {
    return file.originalFileName ?? file.fileName;
  }

  // Non-empty `rejected[]` from the all-rejected 400 body, whether it arrives unwrapped (normal —
  // finance.service rethrows `error.error`) or still wrapped under `err.error`. Undefined for
  // every other error shape so single-code and unknown errors keep the generic handling.
  private rejectedFilesFrom(err: any): CourierInvoiceUploadResult['rejected'] | undefined {
    const rejected = Array.isArray(err?.rejected) ? err.rejected : err?.error?.rejected;
    return Array.isArray(rejected) && rejected.length > 0 ? rejected : undefined;
  }

  // Drops the files the server accepted from both the drawer's own list and the child's displayed
  // chips, matching each selected `File.name` exactly against `accepted[].originalFileName` (the
  // client name echoed back verbatim by app-services). Anything not reported accepted — rejected
  // or, unexpectedly, in neither list — stays selected so the next Save reports it again.
  private pruneAcceptedFiles(result: CourierInvoiceUploadResult): void {
    const acceptedNames = new Set(result.accepted.map((file) => file.originalFileName));
    this.courierInvoiceFiles = this.courierInvoiceFiles.filter((file) => !acceptedNames.has(file.name));
    this.fileUpload?.setFiles(this.courierInvoiceFiles);
  }

  // Every rejected file except the duplicate-invoice ones, which get their own dedicated warning
  // in the drawer body instead of a line in the generic rejection banner.
  private nonDuplicateRejected(
    rejected: CourierInvoiceUploadResult['rejected']
  ): CourierInvoiceUploadResult['rejected'] {
    return rejected.filter((file) => file.code !== 'duplicate-invoice');
  }

  // Rebuilds `duplicateWarnings` from a response: first drops the warning (if any) for every file
  // this response touched — accepted, or rejected again under a possibly-different case — then
  // adds a fresh warning for each file still rejected as 'duplicate-invoice'. Files this response
  // never mentions (e.g. another file from the original selection, untouched by an "Upload
  // anyway" resubmit of just one file) keep their existing warning.
  private updateDuplicateWarnings(result: CourierInvoiceUploadResult): void {
    const touchedNames = new Set<string>([
      ...result.accepted.map((file) => file.originalFileName),
      ...result.rejected.map((file) => file.originalFileName ?? file.fileName),
    ]);
    this.duplicateWarnings = this.duplicateWarnings.filter((warning) => !touchedNames.has(warning.originalFileName));
    const freshWarnings = result.rejected
      .filter((file) => file.code === 'duplicate-invoice' && file.duplicateOf?.length)
      .map((file) => this.buildDuplicateWarning(file));
    this.duplicateWarnings = [...this.duplicateWarnings, ...freshWarnings];
  }

  private buildDuplicateWarning(file: CourierInvoiceUploadResult['rejected'][number]): DuplicateWarningView {
    const entries = file.duplicateOf ?? [];
    return {
      originalFileName: file.originalFileName ?? file.fileName,
      entries,
      // A BillID match is a hard block with no override — only a purely fileName-only match
      // (every entry in `duplicateOf`) gets "Upload anyway".
      canUploadAnyway: entries.length > 0 && entries.every((entry) => entry.matchedBy === 'fileName'),
    };
  }

  // One line of drawer copy per matched row, in the wording finance needs to decide what to do:
  // a BillID match names the earlier file/date and, for processed_with_failures, points at Retry
  // instead of re-uploading; a fileName-only match names the earlier file/date so finance can
  // judge whether "Upload anyway" is safe.
  public getDuplicateEntryMessage(entry: DuplicateOfEntry): string {
    const date = DateTime.fromISO(entry.createdAt).setZone(DefaultTimezone).toFormat(DateFormats.DATE_SHORT);
    if (entry.matchedBy === 'fileName') {
      return `A file with this name was uploaded on ${date} for a different invoice.`;
    }
    const file = entry.originalFileName ?? 'a previously uploaded file';
    if (entry.status === 'processed_with_failures') {
      const failed = entry.rowsFailed ?? 0;
      const countText =
        entry.rowsEnqueued != null ? `${failed} of ${entry.rowsEnqueued} rows failed` : `${failed} rows failed`;
      return (
        `Already uploaded on ${date} (${file}) — ${countText}. Use Retry failed rows on that invoice ` +
        'instead of uploading it again.'
      );
    }
    if (entry.status === 'processed') {
      return `Already uploaded on ${date} (${file}) and fully processed. Nothing to do.`;
    }
    return (
      `Already uploaded on ${date} (${file}) and still being processed. If it has been stuck for ` +
      'more than 2 hours, use Retry on that invoice.'
    );
  }

  // "Upload anyway" — resends ONLY the one fileName-matched file, with `allowNameMatch: true`.
  // `canUploadAnyway` already gates the button to a pure fileName match, so this never needs to
  // (and never could, server-side) override a BillID match.
  public uploadAnyway(warning: DuplicateWarningView): void {
    const file = this.courierInvoiceFiles.find((selected) => selected.name === warning.originalFileName);
    if (!file) {
      return;
    }
    this.loading = true;
    const formValues = this.form.value;
    const params = { courier: formValues.provider, invoiceNumber: formValues.invoiceNumber, allowNameMatch: true };
    const formData = new FormData();
    formData.append('dto', JSON.stringify(params));
    formData.append('courierInvoiceFile', file, file.name);

    this.financeService.uploadCourierInvoice(formData).subscribe({
      next: (res) => this.handleUploadSuccess(res),
      error: (err) => this.handleUploadError(err, false),
    });
  }

  private createForm() {
    const courierOptions = Object.values(SupportedReconProvider)
      .map((provider) => ({
        id: provider,
        text: this.helpersService.convertSupportedReconProvidersEnumToString(provider),
        isMultiLeg: provider.includes('MULTI_LEG'),
      }))
      .filter(
        (option) =>
          option.text !== ShippingProviderNames.UNKNOWN &&
          option.id !== SupportedReconProvider.RIVO &&
          option.id !== SupportedReconProvider.APPLE_EXPRESS_MULTI_LEG &&
          option.id !== SupportedReconProvider.T_FORCE_MULTI_LEG &&
          option.id !== SupportedReconProvider.COURANT_PLUS &&
          option.id !== SupportedReconProvider.GOBOLT &&
          option.id !== SupportedReconProvider.FLASHBOX
      )
      .sort((a, b) => {
        // First sort by multi-leg
        if (a.isMultiLeg !== b.isMultiLeg) {
          return a.isMultiLeg ? 1 : -1;
        }
        // Then sort alphabetically by text
        return a.text.localeCompare(b.text);
      })
      .map(({ id, text }) => ({ id, text })); // Remove isMultiLeg from final objects

    this.issuerOptions.push({
      groupName: 'Couriers',
      options: courierOptions,
    });

    this.form = this.formBuilder.group({
      provider: ['', Validators.required],
      file: ['', Validators.required],
      invoiceNumber: [''],
    });

    this.loading = false;
  }
}
